import React, { useEffect, useMemo } from 'react';
import { Check, Info, AlertTriangle } from 'lucide-react';
import { useSimulateur } from '../SimulateurContext';
import { Button } from '../../ui/button';
import { diagnosticPricing, calculateDiagnosticPrice } from '../../../utils/pricing';

export const Step4Diagnostics = () => {
  const { formData, updateFormData, goToNextStep, goToPrevStep } = useSimulateur();

  // Determine which diagnostics are mandatory based on property info
  const diagnosticInfo = useMemo(() => {
    const { transactionType, propertyType, constructionYear, hasGas } = formData;
    const year = parseInt(constructionYear) || 2000;
    
    const diagnostics = [];
    
    // DPE - Always mandatory
    diagnostics.push({
      key: 'dpe',
      name: 'DPE - Performance Énergétique',
      mandatory: true,
      reason: 'Obligatoire pour toute transaction immobilière',
      icon: '🌱',
    });
    
    // Carrez/Boutin - Based on transaction type
    diagnostics.push({
      key: 'carrez',
      name: transactionType === 'vente' ? 'Loi Carrez' : 'Loi Boutin',
      mandatory: true,
      reason: transactionType === 'vente' 
        ? 'Obligatoire pour la vente en copropriété'
        : 'Obligatoire pour la location',
      icon: '📐',
    });
    
    // Amiante - Before 1997
    if (year < 1997) {
      diagnostics.push({
        key: 'amiante',
        name: 'Diagnostic Amiante',
        mandatory: true,
        reason: 'Obligatoire pour les biens construits avant 1997',
        icon: '⚠️',
      });
    } else {
      diagnostics.push({
        key: 'amiante',
        name: 'Diagnostic Amiante',
        mandatory: false,
        reason: 'Recommandé même pour les biens récents',
        icon: '⚠️',
      });
    }
    
    // Plomb (CREP) - Before 1949
    if (year < 1949) {
      diagnostics.push({
        key: 'plomb',
        name: 'Diagnostic Plomb (CREP)',
        mandatory: true,
        reason: 'Obligatoire pour les biens construits avant 1949',
        icon: '🔬',
      });
    } else {
      diagnostics.push({
        key: 'plomb',
        name: 'Diagnostic Plomb (CREP)',
        mandatory: false,
        reason: 'Recommandé pour les biens anciens',
        icon: '🔬',
      });
    }
    
    // Électricité - If installation > 15 years
    const installationAge = 2024 - year;
    if (installationAge > 15) {
      diagnostics.push({
        key: 'electricite',
        name: 'Diagnostic Électricité',
        mandatory: true,
        reason: 'Obligatoire si installation de plus de 15 ans',
        icon: '⚡',
      });
    } else {
      diagnostics.push({
        key: 'electricite',
        name: 'Diagnostic Électricité',
        mandatory: false,
        reason: 'Recommandé pour la sécurité',
        icon: '⚡',
      });
    }
    
    // Gaz - If has gas installation > 15 years
    if (hasGas) {
      if (installationAge > 15) {
        diagnostics.push({
          key: 'gaz',
          name: 'Diagnostic Gaz',
          mandatory: true,
          reason: 'Obligatoire si installation gaz de plus de 15 ans',
          icon: '🔥',
        });
      } else {
        diagnostics.push({
          key: 'gaz',
          name: 'Diagnostic Gaz',
          mandatory: false,
          reason: 'Recommandé pour les installations récentes',
          icon: '🔥',
        });
      }
    }
    
    // Termites - Always recommended in risk zones
    diagnostics.push({
      key: 'termites',
      name: 'Diagnostic Termites',
      mandatory: false,
      reason: 'Obligatoire dans les zones à risque (Marseille)',
      icon: '🐛',
    });
    
    // ERP - Always mandatory
    diagnostics.push({
      key: 'erp',
      name: 'ERP - Risques et Pollutions',
      mandatory: true,
      reason: 'Obligatoire pour toute transaction',
      icon: '📋',
    });
    
    return diagnostics;
  }, [formData]);

  // Auto-select mandatory diagnostics on mount
  useEffect(() => {
    const mandatoryKeys = diagnosticInfo
      .filter(d => d.mandatory)
      .map(d => d.key);
    
    updateFormData('selectedDiagnostics', mandatoryKeys);
  }, [diagnosticInfo, updateFormData]);

  const handleToggleDiagnostic = (key, mandatory) => {
    if (mandatory) return; // Can't deselect mandatory diagnostics
    
    const current = formData.selectedDiagnostics || [];
    const updated = current.includes(key)
      ? current.filter(k => k !== key)
      : [...current, key];
    
    updateFormData('selectedDiagnostics', updated);
  };

  // Calculate prices
  const calculatePrice = (key) => {
    return calculateDiagnosticPrice(
      key,
      parseFloat(formData.surface) || 50,
      formData.propertyType,
      parseInt(formData.constructionYear)
    );
  };

  const selectedDiagnostics = formData.selectedDiagnostics || [];
  const totalPrice = selectedDiagnostics.reduce((sum, key) => sum + calculatePrice(key), 0);
  
  // Calculate discount
  const count = selectedDiagnostics.length;
  let discountRate = 0;
  if (count >= 4) discountRate = 15;
  else if (count >= 2) discountRate = 10;
  
  const discount = Math.round(totalPrice * (discountRate / 100));
  const finalPrice = totalPrice - discount;

  return (
    <div className="space-y-6">
      <div className="text-center space-y-3">
        <h2 className="text-2xl md:text-3xl font-bold text-stone-900">
          Vos diagnostics
        </h2>
        <p className="text-stone-600">
          Les diagnostics obligatoires sont pré-sélectionnés selon votre situation
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {/* Diagnostics List */}
        <div className="lg:col-span-2 space-y-3">
          {diagnosticInfo.map((diagnostic) => {
            const isSelected = selectedDiagnostics.includes(diagnostic.key);
            const price = calculatePrice(diagnostic.key);
            
            return (
              <button
                key={diagnostic.key}
                onClick={() => handleToggleDiagnostic(diagnostic.key, diagnostic.mandatory)}
                disabled={diagnostic.mandatory}
                data-testid={`diagnostic-${diagnostic.key}`}
                className={`
                  w-full p-4 rounded-xl border-2 transition-all duration-200 text-left
                  ${isSelected 
                    ? 'border-emerald-500 bg-emerald-50' 
                    : 'border-stone-200 hover:border-stone-300'
                  }
                  ${diagnostic.mandatory ? 'cursor-default' : 'cursor-pointer'}
                `}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    {/* Checkbox */}
                    <div className={`
                      w-6 h-6 rounded-md border-2 flex items-center justify-center mt-0.5 flex-shrink-0
                      ${isSelected 
                        ? 'bg-emerald-500 border-emerald-500' 
                        : 'border-stone-300'
                      }
                    `}>
                      {isSelected && <Check className="w-4 h-4 text-white" />}
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{diagnostic.icon}</span>
                        <span className={`font-semibold ${isSelected ? 'text-emerald-700' : 'text-stone-900'}`}>
                          {diagnostic.name}
                        </span>
                        {diagnostic.mandatory && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                            Obligatoire
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-stone-500 mt-1">
                        {diagnostic.reason}
                      </p>
                    </div>
                  </div>
                  
                  <span className={`font-bold text-lg whitespace-nowrap ${isSelected ? 'text-emerald-600' : 'text-stone-700'}`}>
                    {price}€
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Price Summary */}
        <div className="lg:col-span-1">
          <div className="sticky top-6 p-6 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl text-white shadow-xl">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <span>💰</span> Récapitulatif
            </h3>
            
            <div className="space-y-3 mb-4">
              <div className="flex justify-between text-emerald-100">
                <span>{count} diagnostic{count > 1 ? 's' : ''}</span>
                <span>{totalPrice}€</span>
              </div>
              
              {discountRate > 0 && (
                <div className="flex justify-between text-emerald-200">
                  <span>Réduction ({discountRate}%)</span>
                  <span>-{discount}€</span>
                </div>
              )}
            </div>
            
            <div className="border-t border-emerald-500 pt-4">
              <div className="flex justify-between items-center">
                <span className="text-lg">Total TTC</span>
                <span className="text-3xl font-bold">{finalPrice}€</span>
              </div>
            </div>

            {discountRate > 0 && (
              <div className="mt-4 p-3 bg-emerald-500/30 rounded-lg text-sm">
                <span className="font-semibold">🎉 Économie réalisée : {discount}€</span>
              </div>
            )}
          </div>

          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex gap-3">
              <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                Les prix peuvent varier selon les spécificités du bien. Un devis définitif vous sera envoyé par email.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-4 max-w-6xl mx-auto">
        <Button
          type="button"
          variant="outline"
          onClick={goToPrevStep}
          className="px-6 py-3 rounded-xl"
          data-testid="btn-prev-step4"
        >
          Retour
        </Button>
        <Button
          type="button"
          onClick={goToNextStep}
          className="px-8 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="btn-next-step4"
        >
          Continuer
        </Button>
      </div>
    </div>
  );
};
